/**
 * Canonical currency authority for Tinkr. Currency is **first class**.
 *
 * Every monetary value in the system is the pair `(amountMinor: integer, currency)`.
 * This module is the single source of truth for which currency codes the platform
 * recognises, their **minor-unit exponent (decimal places)**, and their **kind**
 * (fiat vs stablecoin).
 *
 * It is a **closed registry of real currencies**, not a shape check:
 *   - Fiat = the active ISO 4217 currencies (the superset of what Stripe accepts),
 *     each with an explicit exponent. This honours "accept any Stripe-supported
 *     currency" — they are all here — without ever treating an arbitrary 3-letter
 *     string (`XYZ`) as a real 2-decimal currency.
 *   - Stablecoins = the explicit accepted set (v1: USDC).
 *
 * Rules:
 *   - **No default currency, ever.** Callers always pass an explicit code.
 *   - **No default/guessed decimals.** Unknown codes return `null`; they are NOT
 *     assumed to be 2-decimal (the "/100 ruins JPY" class of bug). Every recognised
 *     currency carries its own explicit exponent.
 *   - **Fail closed.** Codes outside the registry are rejected; downstream
 *     (Stripe) remains the acceptance authority for which recognised currencies it
 *     will actually process.
 *   - **Zero runtime deps.** Plain TS (no Zod, no Dinero) per the `packages/shared`
 *     convention; `apps/api` wraps `CURRENCY_CODES` with Zod, `apps/web`'s money
 *     wrapper consumes it for decimals (incl. stablecoins Dinero/CLDR lacks).
 *
 * Adding a currency: append its code to the correct exponent group below.
 */

export type CurrencyKind = 'fiat' | 'stablecoin'

// Fiat by minor-unit exponent. Most ISO 4217 currencies are 2 decimals; the
// 0- and 3-decimal sets are the complete exceptions and are the safety-critical
// part of this file (keep them correct).
const FIAT_0_DECIMAL = [
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
] as const

const FIAT_3_DECIMAL = [
  'BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND',
] as const

const FIAT_2_DECIMAL = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN',
  'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CNY', 'COP', 'CRC', 'CUP',
  'CVE', 'CZK', 'DKK', 'DOP', 'DZD', 'EGP', 'ERN', 'ETB', 'EUR', 'FJD',
  'FKP', 'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GTQ', 'GYD', 'HKD', 'HNL',
  'HTG', 'HUF', 'IDR', 'ILS', 'INR', 'IRR', 'JMD', 'KES', 'KGS', 'KHR',
  'KPW', 'KYD', 'KZT', 'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'MAD', 'MDL',
  'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN',
  'MYR', 'MZN', 'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD', 'PAB', 'PEN',
  'PGK', 'PHP', 'PKR', 'PLN', 'QAR', 'RON', 'RSD', 'RUB', 'SAR', 'SBD',
  'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD', 'SSP', 'STN',
  'SVC', 'SYP', 'SZL', 'THB', 'TJS', 'TMT', 'TOP', 'TRY', 'TTD', 'TWD',
  'TZS', 'UAH', 'USD', 'UYU', 'UZS', 'VED', 'VES', 'WST', 'XCD', 'YER',
  'ZAR', 'ZMW', 'ZWG',
] as const

/** Accepted stablecoins (order-payment rail). v1: USDC only, 6 decimals.
 *  Multichain (Base/other ERC-20/Solana) — the chain is a separate dimension on
 *  the payment manifest; the code and decimals are identical across chains. */
const STABLECOIN_MINOR_UNITS = { USDC: 6 } as const

export type FiatCurrencyCode =
  | (typeof FIAT_0_DECIMAL)[number]
  | (typeof FIAT_3_DECIMAL)[number]
  | (typeof FIAT_2_DECIMAL)[number]
export type StablecoinCode = keyof typeof STABLECOIN_MINOR_UNITS
export type CurrencyCode = FiatCurrencyCode | StablecoinCode

export interface CurrencyDef {
  readonly code: CurrencyCode
  readonly kind: CurrencyKind
  /** Minor-unit exponent (decimal places). JPY = 0, USD = 2, BHD = 3, USDC = 6. */
  readonly minorUnits: number
}

function buildRegistry(): Record<string, CurrencyDef> {
  const out: Record<string, CurrencyDef> = {}
  const addFiat = (codes: readonly string[], minorUnits: number) => {
    for (const code of codes) out[code] = { code: code as CurrencyCode, kind: 'fiat', minorUnits }
  }
  addFiat(FIAT_0_DECIMAL, 0)
  addFiat(FIAT_3_DECIMAL, 3)
  addFiat(FIAT_2_DECIMAL, 2)
  for (const [code, minorUnits] of Object.entries(STABLECOIN_MINOR_UNITS)) {
    out[code] = { code: code as CurrencyCode, kind: 'stablecoin', minorUnits }
  }
  return out
}

/** The closed registry, keyed by canonical uppercase code. */
export const CURRENCIES: Readonly<Record<string, CurrencyDef>> = buildRegistry()

/** All recognised currency codes (e.g. for `z.enum(CURRENCY_CODES)` in apps/api). */
export const CURRENCY_CODES = Object.keys(CURRENCIES) as [CurrencyCode, ...CurrencyCode[]]

/** Stablecoin codes accepted in v1. */
export const STABLECOIN_CODES = Object.keys(STABLECOIN_MINOR_UNITS) as StablecoinCode[]

/**
 * Stripe core-country currencies — the buyer pay-in currencies that fall in the
 * LOW ("stripe") forex-fee tier (founder: "pay in stripe[-country] currency 1%").
 *
 * This is the CLASSIFICATION input for the forex fee tier (the fee % itself is
 * admin-editable in `forex_fee_tiers`; this set decides which tier a buyer's
 * pay-in currency maps to). A pay-in currency in this set classifies as the
 * `stripe` tier; any other recognised fiat currency classifies as `other`;
 * stablecoin pay-in is its own tier. It is derived from the well-established
 * Stripe Connect platform-country settlement currencies (the founder's "~40
 * Stripe countries", whose distinct currencies are far fewer because EUR spans
 * many). Edit here — there is intentionally ONE definition consumed by both
 * `apps/api` (the tier classifier) and the MakerChip currency-checker UI, never a
 * parallel list. Every entry MUST be a member of the currency registry above.
 */
const STRIPE_TIER_CURRENCY_CODES = [
  'AUD', 'BGN', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD', 'HUF',
  'JPY', 'MXN', 'MYR', 'NOK', 'NZD', 'PLN', 'RON', 'SEK', 'SGD', 'THB',
  'USD',
] as const

export const STRIPE_TIER_CURRENCIES: ReadonlySet<string> = new Set(
  STRIPE_TIER_CURRENCY_CODES,
)

/**
 * True when a recognised fiat currency falls in the low ("stripe") forex-fee
 * tier. Case-insensitive; returns false for unrecognised or stablecoin codes
 * (stablecoin pay-in is classified separately).
 */
export function isStripeTierCurrency(code: unknown): boolean {
  return STRIPE_TIER_CURRENCIES.has(normalise(code))
}

/** Normalise to canonical uppercase; non-strings → empty so guards fail closed. */
function normalise(code: unknown): string {
  return typeof code === 'string' ? code.toUpperCase() : ''
}

/**
 * True for a recognised currency code (case-insensitive); false otherwise.
 * Plain boolean — deliberately NOT a narrowing type guard, because a lowercase
 * input (`'usd'`) is not the canonical `CurrencyCode` and narrowing it would let
 * callers index `CURRENCIES['usd']` → `undefined`. Canonicalise via
 * `assertCurrency` / `getCurrency` before indexing or storing.
 */
export function isSupportedCurrency(code: unknown): boolean {
  return normalise(code) in CURRENCIES
}

/** Registry entry for `code`, or `null` if unrecognised. Never throws. */
export function getCurrency(code: unknown): CurrencyDef | null {
  const upper = normalise(code)
  return upper in CURRENCIES ? CURRENCIES[upper] : null
}

/** `'fiat'` / `'stablecoin'` for a recognised code, else `null`. */
export function currencyKind(code: unknown): CurrencyKind | null {
  return getCurrency(code)?.kind ?? null
}

/**
 * Minor-unit exponent (decimals) for `code`, or `null` if unrecognised.
 * NEVER assumes a default — callers must handle `null` explicitly.
 */
export function currencyMinorUnits(code: unknown): number | null {
  return getCurrency(code)?.minorUnits ?? null
}

/** True only for accepted stablecoin codes (v1: USDC). */
export function isStablecoin(code: unknown): boolean {
  return getCurrency(code)?.kind === 'stablecoin'
}

/** Throwing accessor for trusted internal call sites that require a recognised code. */
export function assertCurrency(code: unknown): CurrencyCode {
  const info = getCurrency(code)
  if (!info) throw new Error(`Unsupported currency code: ${JSON.stringify(code)}`)
  return info.code
}

export type MajorToMinor =
  | { ok: true; amountMinor: number }
  | { ok: false; reason: 'unsupported_currency' | 'not_finite' | 'fractional_zero_exponent' }

/**
 * Convert a major-unit number to an integer minor-unit amount. Use ONLY at
 * input boundaries (form fields, external JSON); internal math stays in minor
 * units. Fails closed on unrecognised currency, and rejects fractional input for
 * zero-decimal currencies (¥3000.5 does not exist) rather than silently rounding.
 */
export function majorToMinor(amountMajor: number, code: unknown): MajorToMinor {
  const minorUnits = currencyMinorUnits(code)
  if (minorUnits === null) return { ok: false, reason: 'unsupported_currency' }
  if (!Number.isFinite(amountMajor)) return { ok: false, reason: 'not_finite' }
  if (minorUnits === 0 && !Number.isInteger(amountMajor)) {
    return { ok: false, reason: 'fractional_zero_exponent' }
  }
  // Math.round guards against float artifacts (19.99 * 100 = 1998.9999…).
  return { ok: true, amountMinor: Math.round(amountMajor * 10 ** minorUnits) }
}

/**
 * Convert an integer minor-unit amount to its major-unit number. For
 * display/transport only — storage and arithmetic stay in minor units.
 * Returns `null` for an unrecognised currency or a non-integer minor amount.
 */
export function minorToMajor(amountMinor: number, code: unknown): number | null {
  const minorUnits = currencyMinorUnits(code)
  if (minorUnits === null || !Number.isInteger(amountMinor)) return null
  return amountMinor / 10 ** minorUnits
}

/**
 * Locale-aware display string for a minor-unit amount. Known fiat uses
 * `Intl.NumberFormat` currency style; stablecoins (which `Intl` cannot render as
 * a currency) format as `"<major> <CODE>"`. Unrecognised codes fall back to
 * `"<CODE> <amountMinor>"` (raw integer, no invented denominator) so a human sees
 * the row needs attention rather than a wrong number.
 */
export function formatCurrencyMinor(amountMinor: number, code: unknown, locale = 'en'): string {
  const info = getCurrency(code)
  if (!info) return `${normalise(code) || 'XXX'} ${amountMinor}`
  const major = amountMinor / 10 ** info.minorUnits
  if (info.kind === 'fiat') {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: info.code,
        minimumFractionDigits: info.minorUnits,
        maximumFractionDigits: info.minorUnits,
      }).format(major)
    } catch {
      // Intl does not recognise the code in this runtime — fall through.
    }
  }
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: info.minorUnits,
    maximumFractionDigits: info.minorUnits,
  }).format(major)} ${info.code}`
}
